import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, STARTING_LIVES, FONT_STACK, STAR_STACK_SLOTS } from '@/game/config';
import { Crosshair } from '@/game/objects/Crosshair';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { PlanetStack } from '@/game/ui/PlanetStack';
import {
  playStarSound, playAlienSound, playDiamondSound, playFreezeSound, playGalaxySound,
  playSatelliteSound, playPlateSound, playPopSound,
} from '@/game/audio';
import { devFlag, devParam } from '@/game/dev';
import {
  UFO_AT_STACK_STARS, DIAMOND_AT_STACK_STARS, ICE_AT_STACK_STARS, SAT_AT_STACK_STARS,
  CAT_AT_STACK_STARS,
  WORM_AT_STACK_STARS,
  ROCKET_AT_STACK_STARS,
} from '@/game/progression/config';
import { maybeShowDemoSaturnCard } from '@/game/ui/demoSaturnCard';
import type { RoundScene } from './RoundScene';

/**
 * Left edge of the whole top-left HUD stack (menu pill, hearts, score). Tucked close to the canvas
 * edge so the menu button is easy to reach with a thumb; the canvas is letterboxed by ~70 CSS px on
 * a notched phone in landscape (1280×720 FIT), so this never lands under the notch.
 */
const HUD_LEFT = 12;

/**
 * How long the 💎 gets the screen to itself before the demo's end-of-run card arrives over it
 * (see endDemoAtSaturn). Measured from the gem's own landing, which is already a second behind the
 * star chime — long enough for the reward to read as a reward, short enough that the player is
 * still looking at it when the ask appears.
 */
const DEMO_SATURN_CARD_DELAY_MS = 1400;

const HEART_FULL = '♥';
const HEART_EMPTY = '♡';
const HEART_COLOR_FULL = '#ff4d6d';
const HEART_COLOR_EMPTY = '#3a3f47';

function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

export class HUDScene extends Phaser.Scene {
  /** The satellite flourish plays once a run, whichever of its two triggers gets there first. */
  private satFlashed = false;
  /** Same for the plate, which has three triggers (see showPlateOverEclipse). */
  private plateFlashed = false;
  /** A plate has been earned and is waiting for the screen to be free. */
  private platePending = false;
  /**
   * Is a board-break celebration on screen right now? Set by BoardScene's `celebration-start` and
   * cleared by whichever of its two done-beats follows. The plate is the only treat that has to ask
   * — see showPlateOverEclipse.
   */
  private boardCelebrating = false;
  private hearts: Phaser.GameObjects.Text[] = [];
  private starStack!: PlanetStack;
  /** Whole stars shown, so a newly completed one can be chimed and sparkled exactly once. */
  private starsShown = 0;
  /**
   * Bodies added to the real count by the `?stars=N` dev flag. An OFFSET rather than a one-off
   * seed: the flag used to set the column and then lose it, because the very first star the
   * player actually earned fired `stars-changed` with the real session count and setStars drew
   * that instead — 125 points, i.e. about a dozen correct answers. So `?stars=6` was really
   * "six bodies for the next twelve smashes", which is neither what the flag says nor long
   * enough to film. Added to every update instead, so the forced column stays put AND the chime
   * and sparkle still fire on real progress.
   */
  private starsOffset = 0;
  private scoreText!: Phaser.GameObjects.Text;
  private roundText!: Phaser.GameObjects.Text;
  private timeText!: Phaser.GameObjects.Text;
  private bestText!: Phaser.GameObjects.Text;

  private scorePrefix = '';
  private roundPrefix = '';
  private timePrefix = '';
  private bestPrefix = '';

  constructor() {
    super({ key: 'HUD' });
  }

  create(): void {
    this.satFlashed = false;
    this.plateFlashed = false;
    this.platePending = false;
    this.boardCelebrating = false;
    this.scorePrefix = i18next.t('hud.score');
    this.roundPrefix = i18next.t('hud.round');
    this.timePrefix = i18next.t('hud.time');
    this.bestPrefix = i18next.t('hud.best');

    // Back-to-menu button (top-left). Drawn with a soft, rounded background
    // behind the label (Phaser's `backgroundColor` only renders a hard square).
    const backButton = this.add.text(HUD_LEFT, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK,
      fontSize: '27px',
      color: '#aabbcc',
      padding: { x: 22, y: 14 },
    });
    // The tap target is bigger than the pill: at FIT scale on a phone the canvas renders at ~52 %,
    // so the drawn pill is only ~30 CSS px tall — under Apple's 44 pt minimum. Growing it out to
    // the canvas corner (left/up is dead space) plus a margin right/below gets the target to ~44 pt
    // without a chunky-looking button. Local coords, origin (0,0) → negative = outside the text.
    // Nothing else is tappable up here: board words are placed at y ≥ 180 (MARGIN_TOP).
    backButton.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -backButton.x, -backButton.y,
        backButton.width + backButton.x + 14,
        backButton.height + backButton.y + 14,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, backButton);
    backButton.on('pointerdown', () => {
      (this.scene.get('Round') as RoundScene).backToMenu();
    });

    // Lives (row of hearts below the back button). The stack below is positioned off the button's
    // measured height, so resizing the button can't leave it overlapping the hearts.
    const heartsY = backButton.y + backButton.height + 12;
    this.hearts = [];
    for (let i = 0; i < STARTING_LIVES; i++) {
      const heart = this.add.text(HUD_LEFT + i * 36, heartsY, HEART_FULL, {
        fontSize: '40px', color: HEART_COLOR_FULL,
      });
      this.hearts.push(heart);
    }

    this.scoreText = this.add.text(HUD_LEFT, heartsY + this.hearts[0].height + 8, '', {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
    });

    // Round, time, best stacked on the top-right
    this.roundText = this.add.text(GAME_WIDTH - 24, 20, '', {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
    }).setOrigin(1, 0);
    this.timeText = this.add.text(GAME_WIDTH - 24, 58, '', {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
    }).setOrigin(1, 0);
    this.bestText = this.add.text(GAME_WIDTH - 24, 90, '', {
      fontFamily: FONT_STACK, fontSize: '20px', color: '#7ec4ff',
    }).setOrigin(1, 0);

    // Solar system on the RIGHT EDGE, growing upward from near the bottom: Mercury first, the Sun
    // last. Nine slots at a 56px pitch reach y≈232 — and the Sun's halo another ~60px above that —
    // so the column stays clear of the round/time/
    // best readouts above it, and its x sits outside the board's word area (BoardScene keeps tiles
    // within a 140px x-margin). Saturn's ring is the widest body at ~3x its disc radius, so 40px in
    // from the edge is what keeps it on canvas.
    this.starStack = new PlanetStack(this, GAME_WIDTH - 40, GAME_HEIGHT - 40, { slots: STAR_STACK_SLOTS });
    this.starStack.setDepth(40);

    const round = this.scene.get('Round') as RoundScene;
    this.setLives(round.lives);
    this.setScore(round.score);
    this.setRound(round.boardNumber);
    this.setBest(round.bestRoundMs);
    // The column is per-game and counted in score, so it opens empty every time.
    // Dev/staging: `?stars=3` lights three bodies (and `?stars=12` shows the Sun's extra flares)
    // so the column, the chime and the sparkle can be judged without playing for them.
    const forcedStars = Number(devParam('stars'));
    this.starsOffset = Number.isFinite(forcedStars) && forcedStars > 0 ? forcedStars : 0;
    // Seeded straight into starsShown rather than through setStars, so the forced bodies are just
    // *there* — a chime and a pop for stars nobody earned is a reward cue firing at nothing.
    this.starsShown = round.sessionStars + this.starsOffset;
    this.starStack.setProgress(this.starsShown, 0);

    round.events.on('lives-changed', this.setLives, this);
    round.events.on('score-changed', this.setScore, this);
    round.events.on('round-changed', this.setRound, this);
    round.events.on('best-changed', this.setBest, this);
    round.events.on('stars-changed', this.setStars, this);
    round.events.on('ufo-earned', this.showUfoOverMars, this);
    round.events.on('diamond-earned', this.showDiamondOverSaturn, this);
    round.events.on('cat-earned', this.showCatOverVenus, this);
    round.events.on('cube-earned', this.showCubeOverNeptune, this);
    round.events.on('galaxy-earned', this.showGalaxy, this);
    round.events.on('sat-earned', this.showSatelliteOverSun, this);
    round.events.on('pasta-earned', this.showPlateOverEclipse, this);
    round.events.on('worm-earned', this.showWormOverRedSun, this);
    round.events.on('rocket-earned', this.showRocketOverVioletSun, this);
    round.events.on('celebration-start', this.onCelebrationStart, this);
    round.events.on('stack-celebrated', this.onCelebrationEnd, this);
    round.events.on('perfect-celebrated', this.onCelebrationEnd, this);

    // Dev/staging: `?ufo` plays the Mars reward on the first board — the same call the real event
    // makes, delay included, so the badge, its fade and the alien sound can be judged without
    // scoring 500 points for them. Nothing is banked (use `?survival` for a spendable UFO).
    if (devFlag('ufo')) this.showUfoOverMars();
    // Same for `?diamond` → the Saturn reward, without scoring 750 points for it. Nothing is
    // banked (use `?pairs` for a spendable Diamond).
    if (devFlag('diamond')) this.showDiamondOverSaturn();
    // Same for `?icecube` → the Neptune reward, without scoring 1000 points for it.
    // Same for `?catface` → the Venus reward. 25 smashes is a short run, but the flourish should
    // still be judgeable without playing one (use `?cat` for a spendable Cat).
    if (devFlag('catface')) this.showCatOverVenus();
    if (devFlag('icecube')) this.showCubeOverNeptune();
    // Same for `?galaxy` → the top-of-the-ladder reward, without scoring 2125 points for it. This
    // is also the only way anyone sees the flourish before a real player does: 213 smashes is a
    // long run to make twice (use `?wagon` for a spendable Galaxy, and `?stars=17` to see the
    // spiral it lands on).
    if (devFlag('galaxy')) this.showGalaxy();
    if (devFlag('satellite')) this.flashSatelliteNow();
    // `?plate` plays the eclipse reward on the first board — star 14 is 175 smashes away otherwise
    // (use `?recipe` for a spendable Plate, and `?stars=14` to see the rung it lands on).
    if (devFlag('plate')) this.flashPlateNow();
    // Same for `?wormface` → the red-Sun reward, without scoring 1625 points for it. 163 smashes is
    // the longest run of any treat (use `?worm` for a spendable Worm, `?stars=13` for the rung).
    if (devFlag('wormface')) this.showWormOverRedSun();
    // `?rocketface` → the violet-Sun reward (use `?rocket` for a spendable Rocket, `?stars=15`).
    if (devFlag('rocketface')) this.showRocketOverVioletSun();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      round.events.off('lives-changed', this.setLives, this);
      round.events.off('score-changed', this.setScore, this);
      round.events.off('round-changed', this.setRound, this);
      round.events.off('best-changed', this.setBest, this);
      round.events.off('stars-changed', this.setStars, this);
      round.events.off('ufo-earned', this.showUfoOverMars, this);
      round.events.off('diamond-earned', this.showDiamondOverSaturn, this);
      round.events.off('cat-earned', this.showCatOverVenus, this);
      round.events.off('cube-earned', this.showCubeOverNeptune, this);
      round.events.off('galaxy-earned', this.showGalaxy, this);
      round.events.off('sat-earned', this.showSatelliteOverSun, this);
      round.events.off('stack-celebrated', this.flashSatelliteNow, this);
      round.events.off('pasta-earned', this.showPlateOverEclipse, this);
      round.events.off('worm-earned', this.showWormOverRedSun, this);
      round.events.off('rocket-earned', this.showRocketOverVioletSun, this);
      round.events.off('celebration-start', this.onCelebrationStart, this);
      round.events.off('stack-celebrated', this.onCelebrationEnd, this);
      round.events.off('perfect-celebrated', this.onCelebrationEnd, this);
    });

    // Crosshair lives here (HUD renders above Board) at high depth so it draws ON TOP of the
    // HUD's own menu button and hearts — not hidden behind them. It's non-interactive, so it
    // never blocks clicks on the menu button.
    new Crosshair(this);

    // Time readout ticks on a 100ms timer (the displayed precision) rather than update():
    // setText re-rasterizes the text texture whenever the string changes, so per-frame calls
    // just burn CPU/GPU on no-ops between ticks. Scene timers pause with the scene, so the
    // readout still freezes while the Help overlay is open — same as update() did.
    this.updateTime();
    this.time.addEvent({ delay: 100, loop: true, callback: () => this.updateTime() });
  }

  private updateTime(): void {
    const round = this.scene.get('Round') as RoundScene;
    this.timeText.setText(`${this.timePrefix}: ${formatSeconds(round.elapsedMs())}`);
  }

  private setLives(n: number): void {
    for (let i = 0; i < this.hearts.length; i++) {
      const filled = i < n;
      this.hearts[i].setText(filled ? HEART_FULL : HEART_EMPTY);
      this.hearts[i].setColor(filled ? HEART_COLOR_FULL : HEART_COLOR_EMPTY);
    }
  }

  private setScore(n: number): void {
    this.scoreText.setText(`${this.scorePrefix}: ${n}`);
  }

  private setRound(n: number): void {
    // In the App Clip the honest readout is the trial position ("2/3"), not the board number —
    // the cap is visible from the first board rather than sprung at the end. Null everywhere else.
    const trial = (this.scene.get('Round') as RoundScene).clipTrialLabel();
    this.roundText.setText(`${this.roundPrefix}: ${trial ?? n}`);
  }

  private setBest(ms: number | null): void {
    const value = ms === null ? '—' : formatSeconds(ms);
    this.bestText.setText(`${this.bestPrefix}: ${value}`);
  }

  /**
   * Live journey progress: `earned` whole stars this game, `fraction` toward the next one. The
   * chime and sparkle fire only on the *transition*, once per star — this handler runs after every
   * board, and re-announcing a star the player already has would turn the reward into noise.
   */
  private setStars(earned: number, fraction: number): void {
    const shown = earned + this.starsOffset;
    this.starStack.setProgress(shown, fraction);
    if (shown > this.starsShown) {
      // Pop the star that just completed (index is 0-based, so the Nth star is slot N-1).
      this.starStack.popStar(shown - 1);
      playStarSound();
    }
    this.starsShown = shown;
  }

  /**
   * The Mars reward, banked by RoundScene: a UFO drops onto the planet that earned it and fades.
   *
   * Held back a second so it lands AFTER the star chime rather than on top of it. Both fire from
   * the same correct answer — played together they smear into one indistinct noise, and the UFO
   * (the rarer, bigger news) is the half that would be lost.
   */
  private showUfoOverMars(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashUfo(UFO_AT_STACK_STARS - 1);
      playAlienSound();
    });
  }

  /** The Saturn reward — a banked Pairs run (specs/pairs-mode.md §2.3). Held back like the UFO. */
  private showDiamondOverSaturn(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashDiamond(DIAMOND_AT_STACK_STARS - 1);
      playDiamondSound();
      this.endDemoAtSaturn();
    });
  }

  /**
   * The web demo's ending, for a visitor whose store we know (src/game/clip.ts
   * `demoRunsToSaturn`): Saturn is as far as the trial goes, and the Diamond that just landed is
   * what the card is about.
   *
   * Ordered on purpose. The gem is allowed to land and be heard first — the card would otherwise
   * arrive on top of the reward it is naming — and only then does the game stop. **Board and Round
   * are paused, the HUD is not**: a paused scene runs no timers, and the HUD is the scene that owns
   * the card. Nothing resumes them; the only way on from here is the store, which is the point.
   *
   * A no-op everywhere else, including the App Clip (which ends on its own board allowance) and
   * the free web build (which never ends at all).
   */
  private endDemoAtSaturn(): void {
    this.time.delayedCall(DEMO_SATURN_CARD_DELAY_MS, () => {
      const shown = maybeShowDemoSaturnCard(this, { onLeave: () => this.leaveDemoToMenu() });
      if (!shown) return;
      this.scene.pause('Board');
      this.scene.pause('Round');
    });
  }

  /**
   * Where a player lands when they come back from the store.
   *
   * On a phone the store link is caught by the App Store or Play app, so this page is never
   * unloaded — switch back and the card is still there, over a board frozen mid-round, offering
   * the one button that has already been pressed. That is a dead end, so the game leaves the round
   * the moment the store is opened and is sitting on its Menu whenever they return.
   *
   * `backToMenu()` rather than a bare `scene.start`: it is the same exit as the HUD's own ← button
   * and it does the bookkeeping that exit owes — the round is recorded as ended, the session's
   * mastered words are carried to the menu, and the scenes are stopped in the right order (which
   * also destroys this card, since it lives in the HUD's display list). Both scenes are resumed
   * first: a paused scene has to be running again before it can be shut down cleanly.
   */
  private leaveDemoToMenu(): void {
    this.scene.resume('Board');
    this.scene.resume('Round');
    (this.scene.get('Round') as RoundScene).backToMenu();
  }

  /**
   * The Venus reward — a banked Cat & Mouse run (specs/cat-mouse-mode.md §2.3). Same one-second
   * hold as the Mars saucer and the Neptune cube: the child is mid-board, and a badge that lands on
   * the same frame as the star chime is two things arriving at once.
   *
   * This is the earliest flourish the game can ever play — star 2 — so it is also, for most
   * players, the first time the planet ladder is shown to mean something.
   */
  private showCatOverVenus(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashCat(CAT_AT_STACK_STARS - 1);
      playPopSound();
    });
  }

  /** The Neptune reward — a banked Ice Maze run (specs/word-maze-mode.md §2.3). Same hold. */
  private showCubeOverNeptune(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashCube(ICE_AT_STACK_STARS - 1);
      playFreezeSound();
    });
  }

  /**
   * The galaxy — a banked Word Wagon run (specs/wagon-mode.md §2.3). Same hold, and here the hold
   * does a second job: the same star turns the Sun's slot into the spiral, and a second is long
   * enough for that pull-back to have finished, so the badge lands on the galaxy rather than
   * arriving during its own arrival.
   *
   * No slot index — the Galaxy has no planet of its own; `flashGalaxy` uses the last slot, which
   * is what the spiral replaced.
   */
  private showGalaxy(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashGalaxy();
      playGalaxySound();
    });
  }

  /**
   * The full stack — a banked Word Labyrinth run (specs/labyrinth-mode.md §2.3).
   *
   * The only treat that does NOT hold for a second and then flash, because it is the only one that
   * shares its star with something else: filling the stack fires BoardScene's send-off, a rocket
   * and confetti at the board break. Two things arriving at once makes neither legible, so this one
   * waits for `stack-celebrated` — the event BoardScene emits from showTrueStar's own done
   * callback. A real callback rather than a timer guessing how long confetti takes.
   *
   * The `once` is the important word: the send-off fires at most once a run (RoundScene's
   * `stackCelebrated`), and so must this. The fallback delay covers the one case where the star
   * lands but the board never breaks — the last heart going on that very board — so the treat is
   * still announced to a child who earned it.
   */
  private showSatelliteOverSun(): void {
    const round = this.scene.get('Round');
    round.events.once('stack-celebrated', this.flashSatelliteNow, this);
    this.time.delayedCall(6000, () => {
      round.events.off('stack-celebrated', this.flashSatelliteNow, this);
      this.flashSatelliteNow();
    });
  }

  private flashSatelliteNow(): void {
    if (this.satFlashed) return;
    this.satFlashed = true;
    this.starStack.flashSatellite(SAT_AT_STACK_STARS - 1);
    playSatelliteSound();
  }

  private onCelebrationStart(): void {
    this.boardCelebrating = true;
  }

  private onCelebrationEnd(): void {
    this.boardCelebrating = false;
    // A plate that arrived while the screen was busy has been waiting for exactly this.
    if (this.platePending) this.flashPlateNow();
  }

  /**
   * The eclipse — a banked Recipe Kitchen run (specs/recipe-mode.md §2.3).
   *
   * The three older treats simply hold a second behind the star chime, because nothing else can be
   * on screen when they land. The Satellite waits for the full-stack send-off, because it shares
   * that star. This one is between the two and needs both behaviours: star 14 is well past the
   * send-off, but `BoardScene.showPerfectStar` fires on a clean-streak milestone at ANY star — so
   * the collision is possible, and it is not tied to a particular rung.
   *
   * Hence the flag rather than a fixed wait: flash a second behind the chime when the screen is
   * free (the common case, and the same beat as its siblings), and defer to the celebration's own
   * done-event when it is not. The long fallback is the run whose last heart goes during the
   * celebration, so the child who earned the treat is still told.
   */
  /**
   * The red-Sun reward — a banked Space Worm run (specs/space-worm-mode.md §2.3). Same one-second
   * hold as the Cat's: the star chime lands first, the treat a beat behind it.
   */
  private showWormOverRedSun(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashWorm(WORM_AT_STACK_STARS - 1);
      playPopSound();
    });
  }

  /**
   * The violet-Sun reward — a banked Rocket Read run (specs/rocket-read-mode.md §2). The Worm's
   * one-second hold: the star chime lands first, the treat a beat behind it.
   */
  private showRocketOverVioletSun(): void {
    this.time.delayedCall(1000, () => {
      this.starStack.flashRocket(ROCKET_AT_STACK_STARS - 1);
      playPopSound();
    });
  }

  private showPlateOverEclipse(): void {
    this.platePending = true;
    this.time.delayedCall(1000, () => {
      if (this.boardCelebrating) return;   // onCelebrationEnd will fire it
      this.flashPlateNow();
    });
    this.time.delayedCall(8000, () => this.flashPlateNow());
  }

  private flashPlateNow(): void {
    if (this.plateFlashed) return;
    this.plateFlashed = true;
    this.platePending = false;
    // The Sun's slot, not star 14's: the ladder has nine slots and the eclipse is DRAWN over the
    // Sun (PlanetStack.updateEclipse), so this is where the child is already looking.
    this.starStack.flashPasta(STAR_STACK_SLOTS - 1);
    playPlateSound();
  }
}
